import authService from './authService';
import { ensureOk, readData, readList, readPaginated, toQuery } from './apiResponse';
import type { PaginatedList } from '../types/activity';
import type {
  CardTransaction,
  CardTransactionFilter,
  CardTransactionInput,
  CreditCard,
  CreditCardInput,
  CreditCardInvoice,
  CreditCardProjection,
  DeleteScope,
  PayInvoiceInput,
} from '../types/finance';

/**
 * Finanças → Cartões de crédito: cartões, faturas (status derivado pelo backend), itens de fatura,
 * pagamento/desfazer e a projeção (matriz compra parcelada × competência). Valores em centavos com
 * sinal: negativo = compra, positivo = estorno.
 */
class CreditCardService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  // ------------------------------------------------------------------ cartões

  /** GET /credit-cards?include_inactive=1 — com `current_invoice` e `open_total_cents`. */
  async getAll(includeInactive = false): Promise<CreditCard[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/credit-cards${toQuery({ include_inactive: includeInactive ? 1 : undefined })}`);
    return readList<CreditCard>(response, 'Falha ao listar os cartões');
  }

  /** GET /credit-cards/:id */
  async getById(id: number): Promise<CreditCard> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/credit-cards/${id}`);
    return readData<CreditCard>(response, 'Falha ao carregar o cartão');
  }

  /** POST /credit-cards (409 nome repetido; abre os ciclos até o horizonte). */
  async create(input: CreditCardInput): Promise<CreditCard> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/credit-cards', input);
    return readData<CreditCard>(response, 'Falha ao criar o cartão');
  }

  /** PUT /credit-cards/:id — fechamento/vencimento só afetam faturas futuras sem itens; conta/categoria ressincronizam espelhos. */
  async update(id: number, input: CreditCardInput): Promise<CreditCard> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/credit-cards/${id}`, input);
    return readData<CreditCard>(response, 'Falha ao atualizar o cartão');
  }

  /** DELETE /credit-cards/:id — só inativa (faturas e itens ficam). */
  async deactivate(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/credit-cards/${id}`);
    await ensureOk(response, 'Falha ao inativar o cartão');
  }

  /** GET /credit-cards/projection?months=12 */
  async getProjection(months = 12): Promise<CreditCardProjection> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/credit-cards/projection${toQuery({ months })}`);
    const p = await readData<CreditCardProjection>(response, 'Falha ao carregar a projeção');
    return {
      months: p?.months ?? [],
      cards: p?.cards ?? [],
      groups: p?.groups ?? [],
      others: p?.others ?? [],
      totals: p?.totals ?? [],
    };
  }

  // ------------------------------------------------------------------ faturas

  /** GET /credit-cards/:id/invoices?from=AAAA-MM&to=AAAA-MM — padrão 3 meses atrás até +12, ordem cronológica. */
  async getInvoices(cardId: number, from?: string, to?: string): Promise<CreditCardInvoice[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/credit-cards/${cardId}/invoices${toQuery({ from, to })}`);
    return readList<CreditCardInvoice>(response, 'Falha ao listar as faturas');
  }

  /** GET /credit-cards/invoices/:invoiceId — com `items`. */
  async getInvoice(invoiceId: number): Promise<CreditCardInvoice> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/credit-cards/invoices/${invoiceId}`);
    const inv = await readData<CreditCardInvoice>(response, 'Falha ao carregar a fatura');
    return { ...inv, items: inv?.items ?? [] };
  }

  /** POST /credit-cards/invoices/:invoiceId/pay — 409 se já paga; valor menor que o total gera `carryover` na seguinte. */
  async pay(invoiceId: number, input: PayInvoiceInput): Promise<CreditCardInvoice> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/credit-cards/invoices/${invoiceId}/pay`, input);
    return readData<CreditCardInvoice>(response, 'Falha ao registrar o pagamento da fatura');
  }

  /** POST /credit-cards/invoices/:invoiceId/unpay — 409 se a fatura seguinte já estiver paga. */
  async unpay(invoiceId: number): Promise<CreditCardInvoice> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/credit-cards/invoices/${invoiceId}/unpay`);
    return readData<CreditCardInvoice>(response, 'Falha ao desfazer o pagamento da fatura');
  }

  // ------------------------------------------------------------------ itens

  /** GET /credit-cards/transactions (paginado). */
  async listTransactions(filter: CardTransactionFilter = {}): Promise<PaginatedList<CardTransaction>> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(
      `/credit-cards/transactions${toQuery({
        credit_card_id: filter.credit_card_id,
        invoice_id: filter.invoice_id,
        start: filter.start,
        end: filter.end,
        q: filter.q,
        category_id: filter.category_id,
        uncategorized: filter.uncategorized ? 1 : undefined,
        page: filter.page,
        page_size: filter.page_size,
      })}`,
    );
    return readPaginated<CardTransaction>(response, 'Falha ao listar os itens de fatura');
  }

  /** GET /credit-cards/transactions/:id */
  async getTransaction(id: number): Promise<CardTransaction> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/credit-cards/transactions/${id}`);
    return readData<CardTransaction>(response, 'Falha ao carregar o item');
  }

  /** POST /credit-cards/transactions — `installments` cria N itens (um por fatura consecutiva); devolve o 1º (ou a lista). */
  async createTransaction(input: CardTransactionInput): Promise<CardTransaction | CardTransaction[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/credit-cards/transactions', input);
    return readData<CardTransaction | CardTransaction[]>(response, 'Falha ao lançar no cartão');
  }

  /** PUT /credit-cards/transactions/:id (sem `installments`; parcela edita só esta; carryover/interest → 409). */
  async updateTransaction(id: number, input: CardTransactionInput): Promise<CardTransaction> {
    const apiClient = await this.getApiClient();
    const { installments: _omit, ...body } = input;
    const response = await apiClient.put(`/credit-cards/transactions/${id}`, body);
    return readData<CardTransaction>(response, 'Falha ao atualizar o item');
  }

  /** DELETE /credit-cards/transactions/:id?scope=one|future — 409 se a fatura estiver paga. */
  async removeTransaction(id: number, scope: DeleteScope = 'one'): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/credit-cards/transactions/${id}${toQuery({ scope: scope === 'future' ? 'future' : undefined })}`);
    await ensureOk(response, 'Falha ao excluir o item');
  }
}

export const creditCardService = new CreditCardService();
export default creditCardService;
